import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { PlusCircle, Calendar, MapPin, Clock, Users, Search, ListPlus, ClipboardCheck, FileText, AlertCircle, Trash2, BarChart, Plus, Edit, X, Eye } from 'lucide-react';
import { SessionDetails, SessionType, AttendanceStatus, ReportType } from '../types';
import { format, isAfter, isBefore, isToday, addDays } from 'date-fns';
import SessionPlanningReport from '../components/SessionPlanningReport';
import AttendanceReportModal from '../components/AttendanceReportModal';
import FloatingActionButton from '../components/FloatingActionButton';
import { getSessions, deleteSession } from '../services/sessionService';
import toast from 'react-hot-toast';

const Sessions: React.FC = () => {
  const [sessions, setSessions] = useState<SessionDetails[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState<'all' | 'upcoming' | 'past'>('upcoming');
  const [showReportModal, setShowReportModal] = useState(false);
  const [showAttendanceReportModal, setShowAttendanceReportModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(null);
  const [reportType, setReportType] = useState<ReportType>('planning');
  const [showReportMenu, setShowReportMenu] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    loadSessions();
  }, []);

  const loadSessions = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await getSessions();
      setSessions(data);
    } catch (err) {
      console.error('Error loading sessions:', err);
      setError('Failed to load sessions. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteSession = async (sessionId: string) => {
    try {
      await deleteSession(sessionId);
      setSessions(sessions.filter(session => session.id !== sessionId));
      setShowDeleteConfirm(null);
      toast.success('Session deleted successfully');
    } catch (err) {
      console.error('Error deleting session:', err);
      toast.error('Failed to delete session');
    }
  };

  const isUpcomingSession = (session: SessionDetails): boolean => {
    const sessionDate = new Date(session.date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const tomorrow = addDays(today, 1);
    return isAfter(sessionDate, tomorrow) || isToday(sessionDate);
  };

  const canMarkAttendance = (session: SessionDetails): boolean => {
    return isToday(new Date(session.date)) && session.attendanceStatus !== 'marked';
  };

  const openReportModal = (type: ReportType) => {
    setReportType(type);
    if (type === 'planning') {
      setShowReportModal(true);
    } else {
      setShowAttendanceReportModal(true);
    }
    setShowReportMenu(false);
  };

  const filteredSessions = sessions
    .filter(session => {
      const matchesSearch =
        session.location.toLowerCase().includes(searchTerm.toLowerCase()) ||
        session.type.toLowerCase().includes(searchTerm.toLowerCase()) ||
        session.squads.some(squad => squad.toLowerCase().includes(searchTerm.toLowerCase()));

      if (!matchesSearch) return false;

      const sessionDate = new Date(session.date);
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      switch (filter) {
        case 'upcoming':
          return isAfter(sessionDate, today) || isToday(sessionDate);
        case 'past':
          return isBefore(sessionDate, today) && !isToday(sessionDate);
        default:
          return true;
      }
    })
    .sort((a, b) => {
      const dateA = new Date(a.date);
      const dateB = new Date(b.date);

      if (filter === 'past') {
        return dateB.getTime() - dateA.getTime();
      } else {
        return dateA.getTime() - dateB.getTime();
      }
    });

  if (isLoading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center">
          <p className="text-gray-500">Loading sessions...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-red-50 border-l-4 border-red-400 p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <AlertCircle className="h-5 w-5 text-red-400" />
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-700">{error}</p>
              <button
                className="mt-2 text-sm font-medium text-red-700 hover:text-red-600"
                onClick={loadSessions}
              >
                Try again
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="flex justify-between items-center mb-6 flex-wrap gap-4">
        <h1 className="text-3xl font-bold text-gray-900">Sessions</h1>
        <div className="flex space-x-2 md:flex hidden">
          <div className="relative">
            <button
              className="btn-outline flex items-center"
              onClick={() => setShowReportMenu(!showReportMenu)}
            >
              <FileText size={20} className="mr-2" />
              <span className="hidden sm:inline">Reports</span>
              <span className="sm:hidden">Reports</span>
              <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
              </svg>
            </button>
            {showReportMenu && (
              <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg overflow-hidden z-20">
                <div className="py-1">
                  <button
                    onClick={() => openReportModal('planning')}
                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  >
                    Session Planning Report
                  </button>
                  <button
                    onClick={() => openReportModal('attendance')}
                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  >
                    Attendance Report
                  </button>
                </div>
              </div>
            )}
          </div>
          <Link to="/sessions/bulk-create" className="btn-outline flex items-center">
            <ListPlus size={20} className="mr-2" />
            <span className="hidden sm:inline">Bulk Create</span>
            <span className="sm:hidden">Bulk</span>
          </Link>
          <Link to="/sessions/new" className="btn-primary flex items-center">
            <PlusCircle size={20} className="mr-2" />
            <span className="hidden sm:inline">New Session</span>
            <span className="sm:hidden">New</span>
          </Link>
        </div>
      </div>

      <div className="mb-6 space-y-4">
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search size={20} className="text-gray-400" />
          </div>
          <input
            type="text"
            placeholder="Search sessions..."
            className="pl-10 form-input"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="flex space-x-2 overflow-x-auto pb-2 mobile-scroll">
          <button
            className={`px-4 py-2 rounded-md ${filter === 'all'
                ? 'bg-navy-800 text-white'
                : 'bg-white text-navy-800 border border-navy-800'
              }`}
            onClick={() => setFilter('all')}
          >
            All
          </button>
          <button
            className={`px-4 py-2 rounded-md ${filter === 'upcoming'
                ? 'bg-navy-800 text-white'
                : 'bg-white text-navy-800 border border-navy-800'
              }`}
            onClick={() => setFilter('upcoming')}
          >
            Upcoming
          </button>
          <button
            className={`px-4 py-2 rounded-md ${filter === 'past'
                ? 'bg-navy-800 text-white'
                : 'bg-white text-navy-800 border border-navy-800'
              }`}
            onClick={() => setFilter('past')}
          >
            Past
          </button>
        </div>
      </div>

      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        <ul className="divide-y divide-gray-200">
          {filteredSessions.length > 0 ? (
            filteredSessions.map((session) => {
              const sessionDate = new Date(session.date);
              const today = new Date();
              today.setHours(0, 0, 0, 0);
              const isPast = isBefore(sessionDate, today) && !isToday(sessionDate);
              const isTodays = isToday(sessionDate);
              const showMarkAttendance = canMarkAttendance(session);
              const isUpcoming = isUpcomingSession(session);

              return (
                <li key={session.id}>
                  <div className="block hover:bg-gray-50">
                    <div className="px-4 py-4 sm:px-6">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <Calendar className="h-6 w-6 text-navy-800" />
                          <p className="ml-3 text-sm font-medium text-navy-800">
                            {format(new Date(session.date), 'MMM d, yyyy')}
                          </p>
                        </div>
                        <div className="ml-2 flex-shrink-0 flex">
                          <p className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getSessionTypeColor(session.type)}`}>
                            {getSessionTypeLabel(session.type)}
                          </p>
                        </div>
                      </div>
                      <div className="mt-2 sm:flex sm:justify-between">
                        <div className="sm:flex sm:flex-col">
                          <div className="flex items-center text-sm text-gray-500">
                            <MapPin className="flex-shrink-0 mr-1.5 h-5 w-5 text-gray-400" />
                            {session.location}
                          </div>
                          <div className="mt-2 flex items-center text-sm text-gray-500 sm:mt-2">
                            <Clock className="flex-shrink-0 mr-1.5 h-5 w-5 text-gray-400" />
                            {session.time} ({session.duration} hrs)
                          </div>
                        </div>
                        <div className="mt-2 flex items-center text-sm text-gray-500 sm:mt-0">
                          <Users className="flex-shrink-0 mr-1.5 h-5 w-5 text-gray-400" />
                          <p>
                            {session.squads.join(', ')}
                          </p>
                        </div>
                      </div>

                      <div className="mt-3 flex flex-wrap gap-2">
                        {showMarkAttendance && (
                          <Link
                            to={`/sessions/${session.id}/attendance`}
                            className="inline-flex items-center px-3 py-1 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-navy-800 hover:bg-navy-900 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-navy-500"
                          >
                            <ClipboardCheck size={16} className="mr-1" />
                            <span className="hidden sm:inline">Mark Attendance</span>
                            <span className="sm:hidden">Attendance</span>
                          </Link>
                        )}

                        {isUpcoming && !session.locked ? (
                          <>
                            <Link
                              to={`/sessions/${session.id}`}
                              className="inline-flex items-center px-3 py-1 border border-navy-800 text-sm leading-4 font-medium rounded-md text-navy-800 bg-white hover:bg-navy-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-navy-500"
                            >
                              <Edit size={16} className="mr-1" />
                              <span>Edit</span>
                            </Link>
                            <Link
                              to={`/sessions/${session.id}/view`}
                              className="inline-flex items-center px-3 py-1 border border-navy-800 text-sm leading-4 font-medium rounded-md text-navy-800 bg-white hover:bg-navy-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-navy-500"
                            >
                              <Eye size={16} className="mr-1" />
                              <span>View</span>
                            </Link>
                          </>
                        ) : (
                          <Link
                            to={`/sessions/${session.id}/summary`}
                            className="inline-flex items-center px-3 py-1 border border-navy-800 text-sm leading-4 font-medium rounded-md text-navy-800 bg-white hover:bg-navy-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-navy-500"
                          >
                            <span>View Summary</span>
                          </Link>
                        )}

                        <button
                          onClick={() => setShowDeleteConfirm(session.id)}
                          className="inline-flex items-center px-3 py-1 border border-red-600 text-sm leading-4 font-medium rounded-md text-red-600 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                        >
                          <Trash2 size={16} className="mr-1" />
                          <span className="hidden sm:inline">Delete</span>
                        </button>
                      </div>
                    </div>
                  </div>
                </li>
              );
            })
          ) : (
            <li className="px-4 py-6 text-center text-gray-500">
              No sessions found. Try adjusting your search or create a new session.
            </li>
          )}
        </ul>
      </div>

      <FloatingActionButton to="/sessions/new" />

      <div className="fixed bottom-20 right-4 flex flex-col space-y-2 md:hidden">
        {showReportMenu && (
          <>
            <button
              onClick={() => openReportModal('planning')}
              className="bg-white text-navy-800 rounded-full shadow-lg flex items-center justify-center w-12 h-12 border border-gray-200"
            >
              <FileText size={20} />
            </button>
            <button
              onClick={() => openReportModal('attendance')}
              className="bg-white text-navy-800 rounded-full shadow-lg flex items-center justify-center w-12 h-12 border border-gray-200"
            >
              <BarChart size={20} />
            </button>
            <Link
              to="/sessions/bulk-create"
              className="bg-white text-navy-800 rounded-full shadow-lg flex items-center justify-center w-12 h-12 border border-gray-200"
            >
              <ListPlus size={20} />
            </Link>
          </>
        )}
        <button
          onClick={() => setShowReportMenu(!showReportMenu)}
          className="bg-navy-800 text-white rounded-full shadow-lg flex items-center justify-center w-12 h-12"
        >
          {showReportMenu ? <X size={20} /> : <Plus size={20} />}
        </button>
      </div>

      {showReportModal && (
        <SessionPlanningReport onClose={() => setShowReportModal(false)} />
      )}

      {showAttendanceReportModal && (
        <AttendanceReportModal onClose={() => setShowAttendanceReportModal(false)} />
      )}

      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-auto p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Confirm Deletion</h3>
            <p className="text-sm text-gray-500 mb-4">
              Are you sure you want to delete this session? This action cannot be undone.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                className="btn-outline"
                onClick={() => setShowDeleteConfirm(null)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="bg-red-600 hover:bg-red-700 text-white font-semibold py-2 px-4 rounded-md transition-colors active:bg-red-800"
                onClick={() => handleDeleteSession(showDeleteConfirm)}
              >
                Delete Session
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const getSessionTypeLabel = (type: SessionType) => {
  switch (type) {
    case 'Training':
      return 'Training';
    case 'Tourney Match':
      return 'Match';
    case 'Practice Match':
      return 'Practice Match';
    default:
      return type;
  }
};

const getSessionTypeColor = (type: SessionType) => {
  switch (type) {
    case 'Training':
      return 'bg-navy-100 text-navy-800';
    case 'Tourney Match':
      return 'bg-red-100 text-red-600';
    case 'Practice Match':
      return 'bg-green-100 text-green-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

export default Sessions;